/**
 * Note: Before developing this content,
 * you must first define the theme token values the component.
 */
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
const componentName = 'Input';
export const inputClassPrefix = `ui-Input`;
export const textAreaClassPrefix = 'ui-textarea';
export const passwordClassPrefix = 'ui-password';
const ignoreSsrWarning = '/* emotion-disable-server-rendering-unsafe-selector-warning-please-do-not-use-this-the-warning-exists-for-a-reason */';
export const inputClasses = {
    // It is recommended that the root classname of the react-ui component be added with a random string.
    // This is mainly to avoid classname naming conflicts in the application
    root: `${inputClassPrefix}-root-${randomStr()}`,
    rootError: `${inputClassPrefix}-root-error`,
    rootWarning: `${inputClassPrefix}-root-warning`,
    input: `${inputClassPrefix}-input`,
    inputDisabled: `${inputClassPrefix}-input-disabled`,
    inputReadOnly: `${inputClassPrefix}-input-readOnly`,
    label: `${inputClassPrefix}-label`,
    helperText: `${inputClassPrefix}-helper-text`,
    required: `${inputClassPrefix}-required`,
    optional: `${inputClassPrefix}-optional`,
    affix: `${inputClassPrefix}-affix-wrapper`,
    affixFocused: `${inputClassPrefix}-affix-wrapper-focused`,
    affixDisabled: `${inputClassPrefix}-affix-wrapper-disabled`,
    affixReadOnly: `${inputClassPrefix}-affix-wrapper-readonly`,
    suffix: `${inputClassPrefix}-suffix`,
    prefix: `${inputClassPrefix}-prefix`,
    clearIcon: `${inputClassPrefix}-clear-icon`,
    clearIconHasSuffix: `${inputClassPrefix}-clear-icon-has-suffix`,
    searchIcon: `${inputClassPrefix}-search-icon`,
    readOnly: `${inputClassPrefix}-readOnly`,
    disabled: `${inputClassPrefix}-disabled`,
    error: `${inputClassPrefix}-error`,
    warning: `${inputClassPrefix}-warning`,
    group: `${inputClassPrefix}-group`,
    groupFocused: `${inputClassPrefix}-group-focused`,
    groupDisabled: `${inputClassPrefix}-group-disabled`,
    addon: `${inputClassPrefix}-group-addon`,
    showCount: `${inputClassPrefix}-show-count-suffix`,
    clearIconHidden: `${inputClassPrefix}-clear-icon-hidden`,
    loadingIcon: `${inputClassPrefix}-loading-icon`,
};
export const textAreaClasses = {
    root: `${textAreaClassPrefix}-root-${randomStr()}`,
    rootReadOnly: `${textAreaClassPrefix}-root-readOnly`,
    rootShowCount: `${textAreaClassPrefix}-root-show-count`,
    rootShowCountPosition: `${textAreaClassPrefix}-root-show-count-position`,
    affix: `${textAreaClassPrefix}-affix-wrapper`,
    affixFocused: `${textAreaClassPrefix}-affix-wrapper-focused`,
    affixDisabled: `${textAreaClassPrefix}-affix-wrapper-disabled`,
    textArea: `${textAreaClassPrefix}-textArea`,
    textAreaDisabled: `${textAreaClassPrefix}-disabled`,
    clearIcon: `${textAreaClassPrefix}-clear-icon`,
    suffix: `${textAreaClassPrefix}-suffix`,
    allowClear: `${textAreaClassPrefix}-allow-clear`,
    showCount: `${textAreaClassPrefix}-show-count-suffix`,
    showCountPosition: `${textAreaClassPrefix}-show-count-position`,
    clearIconHidden: `${textAreaClassPrefix}-clear-icon-hidden`,
    rootError: `${textAreaClassPrefix}-root-error`,
    rootWarning: `${textAreaClassPrefix}-root-warning`,
};
export const passwordClasses = {
    password: 'ui-password',
    visibleIcon: 'ui-password-visible-icon',
};
export const searchClasses = {
    search: 'ui-search',
    searchIcon: 'ui-search-icon-search',
};
export function getStyle(theme, opts) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const { status, shouldRenderAffix, size = 'medium' } = opts;
    const statusStyle = (() => {
        let errorStyle = {};
        let warningStyle = {};
        if (status === 'error') {
            errorStyle = shouldRenderAffix
                ? {
                    [`.${inputClasses.affix}`]: {
                        borderColor: tk(token.error.borderColor),
                        [`&.${inputClasses.affixDisabled}`]: {
                            borderColor: tk(token.disabled.error.borderColor),
                        },
                    },
                }
                : {
                    [`.${inputClasses.input}`]: {
                        borderColor: tk(token.error.borderColor),
                        [`&.${inputClasses.inputDisabled}`]: {
                            borderColor: tk(token.disabled.error.borderColor),
                        },
                    },
                };
        }
        if (status === 'warning') {
            warningStyle = shouldRenderAffix
                ? {
                    [`.${inputClasses.affix}`]: {
                        borderColor: tk(token.warning.borderColor),
                        [`&.${inputClasses.affixDisabled}`]: {
                            borderColor: tk(token.disabled.warning.borderColor),
                        },
                    },
                }
                : {
                    [`.${inputClasses.input}`]: {
                        borderColor: tk(token.warning.borderColor),
                        [`&.${inputClasses.inputDisabled}`]: {
                            borderColor: tk(token.disabled.warning.borderColor),
                        },
                    },
                };
        }
        return {
            [`&.${inputClasses.rootError}`]: errorStyle,
            [`&.${inputClasses.rootWarning}`]: warningStyle,
        };
    })();
    const style = {
        [`&.${inputClasses.root}`]: {
            lineHeight: tk(token.Input.height[size]),
            maxWidth: '100%',
            [`.${inputClasses.group}`]: {
                display: 'flex',
                margin: 0,
                padding: 0,
                width: '100%',
                height: tk(token.Input.height[size]),
                position: 'relative',
                [`.${inputClasses.input}`]: {
                    flex: '1 0 0',
                    minWidth: 0,
                    '&:focus': {
                        outline: 'none',
                    },
                },
                // The element is inside `rc-input` and it's not possible for emotion
                // to inject style elements into the group. Should be safe here in SSR.
                [`& > .${inputClasses.input}:not(:first-child)${ignoreSsrWarning}`]: {
                    borderStartStartRadius: 0,
                    borderEndStartRadius: 0,
                },
                [`& > .${inputClasses.input}:not(:last-child)`]: {
                    borderStartEndRadius: 0,
                    borderEndEndRadius: 0,
                },
                // The element is inside `rc-input` and it's not possible for emotion
                // to inject style elements into the group. Should be safe here in SSR.
                [`& > .${inputClasses.affix}:not(:first-child)${ignoreSsrWarning}`]: {
                    borderStartStartRadius: 0,
                    borderEndStartRadius: 0,
                },
                [`& > .${inputClasses.affix}:not(:last-child)`]: {
                    borderStartEndRadius: 0,
                    borderEndEndRadius: 0,
                },
                [`.${inputClasses.addon}`]: {
                    flex: '0 1 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    backgroundColor: tk(token.addon.backgroundColor),
                    color: tk(token.addon.color),
                    border: tk(token.addon.border),
                    borderColor: tk(token.addon.borderColor),
                    borderRadius: tk(token.Input.borderRadius[size]),
                    padding: tk(token.addon.padding[size]),
                    lineHeight: tk(token.addon.lineHeight),
                    fontFamily: tk(token.fontFamily),
                    fontSize: tk(token.fontSize[size]),
                    fontWeight: tk(token.fontWeight),
                    // The element is inside `rc-input` and it's not possible for emotion
                    // to inject style elements into the group. Should be safe here in SSR.
                    [`&:first-child${ignoreSsrWarning}`]: {
                        borderStartEndRadius: 0,
                        borderEndEndRadius: 0,
                        borderInlineEnd: 0,
                    },
                    '&:last-child': {
                        borderStartStartRadius: 0,
                        borderEndStartRadius: 0,
                        borderInlineStart: 0,
                    },
                },
            },
            [`.${inputClasses.affix}`]: {
                display: 'inline-flex',
                verticalAlign: 'top',
                height: tk(token.Input.height[size]),
                padding: tk(token.affix.padding[size]),
                border: tk(token.affix.border),
                borderColor: tk(token.affix.borderColor),
                borderRadius: tk(token.Input.borderRadius[size]),
                boxSizing: 'border-box',
                alignItems: 'center',
                width: '100%',
                backgroundColor: tk(token.affix.backgroundColor),
                [`&:hover:not(:focus, .${inputClasses.disabled} *, .${inputClasses.readOnly} *)`]: {
                    backgroundColor: tk(token.hover.backgroundColor),
                    [`.${inputClasses.input}`]: {
                        backgroundColor: 'unset',
                    },
                },
                [`&.${inputClasses.affixFocused}`]: {
                    outline: tk(token.Input.focus.outline),
                    outlineOffset: tk(token.Input.focus.outlineOffset),
                    [`&:hover:not(:focus, .${inputClasses.affixDisabled}, .${inputClasses.affixReadOnly})`]: {
                        backgroundColor: token.Input.backgroundColor,
                    },
                },
                [`&.${inputClasses.affixReadOnly}`]: {
                    color: tk(token.readOnly.color),
                    backgroundColor: tk(token.readOnly.backgroundColor),
                },
                [`.${inputClasses.input}`]: {
                    height: '100%',
                    width: '100%',
                    padding: 0,
                    border: 'none',
                    borderColor: 'unset',
                    borderRadius: 0,
                    flex: '1 1 auto',
                    '&:focus': {
                        outline: 'none',
                    },
                },
                [`.${inputClasses.prefix}`]: {
                    display: 'flex',
                    height: '100%',
                    alignItems: 'center',
                    marginInlineEnd: tk(token.prefix.marginInlineEnd),
                    fontSize: tk(token.fontSize[size]),
                    color: tk(token.icon.fill),
                    // '.zmicon': {
                    //   display: 'inline-flex',
                    //   alignItems: 'center',
                    // },
                },
                [`.${inputClasses.suffix}`]: {
                    display: 'flex',
                    height: '100%',
                    alignItems: 'center',
                    marginInlineStart: tk(token.suffix.marginInlineStart),
                    flex: '0 0 auto',
                    fontSize: tk(token.fontSize[size]),
                    color: tk(token.icon.fill),
                },
                [`.${searchClasses.searchIcon}`]: {
                    color: tk(token.searchIcon.fill),
                },
                [`.${inputClasses.clearIcon}`]: {
                    cursor: 'pointer',
                    flex: '0 0 auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    marginInlineEnd: '-6px',
                    svg: {
                        width: 14,
                        height: 14,
                        viewBox: '0 0 14 14',
                    },
                    path: {
                        fill: tk(token.clearIcon.fill),
                    },
                    [`&.${inputClasses.clearIconHasSuffix}`]: {
                        marginInlineEnd: tk(token.clearIcon.marginRight),
                    },
                    [`&.${inputClasses.clearIconHidden}`]: {
                        display: 'none',
                    },
                },
                [`.${passwordClasses.visibleIcon}`]: {
                    cursor: 'pointer',
                    marginInlineEnd: '-6px',
                    svg: {
                        path: {
                            fill: tk(token.visibleIcon.fill),
                        },
                    },
                },
                [`&.${inputClasses.affixDisabled}`]: {
                    color: tk(token.disabled.color),
                    borderColor: tk(token.disabled.borderColor),
                    backgroundColor: tk(token.disabled.backgroundColor),
                    [`.${inputClasses.prefix}, .${inputClasses.suffix}, .${searchClasses.searchIcon}, .${inputClasses.loadingIcon}`]: {
                        color: tk(token.disabled.color),
                    },
                    [`.${passwordClasses.visibleIcon}`]: {
                        pointerEvents: 'none',
                        svg: {
                            path: {
                                fill: tk(token.disabled.color),
                            },
                        },
                    },
                },
                [`.${inputClasses.showCount}`]: {
                    color: tk(token.showCount.color),
                    lineHeight: tk(token.showCount.lineHeight),
                    fontFamily: tk(token.fontFamily),
                    fontSize: tk(token.showCount.fontSize),
                    fontWeight: tk(token.showCount.fontWeight),
                    flex: '1 0 auto',
                },
            },
            [`.${inputClasses.groupFocused}`]: {
                [`.${inputClasses.group}`]: {
                    borderRadius: tk(token.Input.borderRadius[size]),
                    outline: tk(token.Input.focus.outline),
                    outlineOffset: tk(token.Input.focus.outlineOffset),
                    [`.${inputClasses.affixFocused}`]: {
                        outline: 'none',
                    },
                },
            },
            [`.${inputClasses.groupDisabled}`]: {
                [`.${inputClasses.addon}, .${inputClasses.affix}`]: {
                    color: tk(token.disabled.color),
                    borderColor: tk(token.disabled.borderColor),
                },
            },
            [`.${inputClasses.input}`]: {
                verticalAlign: 'top',
                height: tk(token.Input.height[size]),
                padding: tk(token.Input.padding[size]),
                border: tk(token.Input.border),
                borderColor: tk(token.Input.borderColor),
                borderRadius: tk(token.Input.borderRadius[size]),
                color: tk(token.Input.color),
                fontFamily: tk(token.fontFamily),
                fontSize: tk(token.fontSize[size]),
                fontWeight: tk(token.fontWeight),
                backgroundColor: tk(token.Input.backgroundColor),
                width: '100%',
                boxSizing: 'border-box',
                '&::placeholder': {
                    color: tk(token.Input.placeholderColor),
                },
                '&:disabled::placeholder': {
                    color: tk(token.disabled.color),
                },
                [`&:hover:not(:focus, .${inputClasses.inputDisabled}, .${inputClasses.inputReadOnly})`]: {
                    backgroundColor: tk(token.hover.backgroundColor),
                },
                [`&.${inputClasses.inputReadOnly}`]: {
                    color: tk(token.readOnly.color),
                    backgroundColor: tk(token.readOnly.backgroundColor),
                },
                [`&.${inputClasses.inputDisabled}`]: {
                    color: tk(token.disabled.color),
                    borderColor: tk(token.disabled.borderColor),
                    backgroundColor: tk(token.disabled.backgroundColor),
                },
                '&:focus-visible': {
                    borderColor: tk(token.Input.borderColor),
                },
                '&:focus': {
                    outline: tk(token.Input.focus.outline),
                    outlineOffset: tk(token.Input.focus.outlineOffset),
                },
            },
            [`.${inputClasses.label}`]: {
                color: tk(token.label.color),
                lineHeight: tk(token.label.lineHeight),
                fontSize: tk(token.label.fontSize),
                marginBottom: 2,
                height: tk(token.label.height),
                baseAlign: 'vertical',
                [`&.${inputClasses.required}::after`]: {
                    display: 'inline-block',
                    color: tk(token.required.color),
                    lineHeight: tk(token.required.lineHeight),
                    content: '"*"',
                    fontSize: tk(token.required.fontSize),
                    marginInlineStart: tk(token.required.marginLeft),
                },
                [`&.${inputClasses.required}::after`]: {
                    display: 'inline-block',
                    color: tk(token.optional.color),
                    lineHeight: tk(token.optional.lineHeight),
                    content: '"(Optional)"',
                    fontSize: tk(token.optional.fontSize),
                    marginInlineStart: tk(token.optional.marginLeft),
                },
            },
            [`.${inputClasses.helperText}`]: {
                color: tk(token.helperText.color),
                lineHeight: tk(token.helperText.lineHeight),
                fontSize: tk(token.helperText.fontSize),
                paddingLeft: tk(token.helperText.paddingLeft),
                height: tk(token.helperText.height),
            },
            ...statusStyle,
        },
    };
    return style;
}
export function getTextAreaStyle(theme, opts) {
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const { status, shouldRenderAffix, size = 'medium', resizable } = opts;
    const statusStyle = (() => {
        let errorStyle = {};
        let warningStyle = {};
        if (status === 'error') {
            errorStyle = shouldRenderAffix
                ? {
                    [`.${textAreaClasses.affix}`]: {
                        borderColor: tk(token.error.borderColor),
                        [`&.${textAreaClasses.affixDisabled}`]: {
                            borderColor: tk(token.disabled.error.borderColor),
                        },
                    },
                }
                : {
                    [`.${textAreaClasses.textArea}`]: {
                        borderColor: tk(token.error.borderColor),
                        [`&.${textAreaClasses.textAreaDisabled}`]: {
                            borderColor: tk(token.disabled.error.borderColor),
                        },
                    },
                };
        }
        if (status === 'warning') {
            warningStyle = shouldRenderAffix
                ? {
                    [`.${textAreaClasses.affix}`]: {
                        borderColor: tk(token.warning.borderColor),
                        [`&.${textAreaClasses.affixDisabled}`]: {
                            borderColor: tk(token.disabled.warning.borderColor),
                        },
                    },
                }
                : {
                    [`.${textAreaClasses.textArea}`]: {
                        borderColor: tk(token.warning.borderColor),
                        [`&.${textAreaClasses.textAreaDisabled}`]: {
                            borderColor: tk(token.disabled.warning.borderColor),
                        },
                    },
                };
        }
        return {
            [`&.${textAreaClasses.rootError}`]: errorStyle,
            [`&.${textAreaClasses.rootWarning}`]: warningStyle,
        };
    })();
    const style = {
        [`&.${textAreaClasses.root}`]: {
            [`.${textAreaClasses.textArea}`]: {
                width: '100%',
                height: tk(token.TextArea.height[size]),
                padding: tk(token.TextArea.padding[size]),
                border: tk(token.TextArea.border),
                borderColor: tk(token.TextArea.borderColor),
                borderRadius: tk(token.TextArea.borderRadius[size]),
                color: tk(token.TextArea.color),
                lineHeight: tk(token.TextArea.lineHeight[size]),
                fontFamily: tk(token.fontFamily),
                fontSize: tk(token.fontSize[size]),
                fontWeight: tk(token.fontWeight),
                backgroundColor: tk(token.TextArea.backgroundColor),
                boxSizing: 'border-box',
                display: 'block',
                '&::placeholder': {
                    color: tk(token.TextArea.placeholderColor),
                },
                '&:disabled::placeholder': {
                    color: tk(token.disabled.color),
                },
                [`&:hover:not(:focus, .${textAreaClasses.textAreaDisabled}, .${textAreaClasses.rootReadOnly} *)`]: {
                    backgroundColor: tk(token.hover.backgroundColor),
                },
                '&:focus': {
                    outline: tk(token.Input.focus.outline),
                    outlineOffset: tk(token.Input.focus.outlineOffset),
                },
                [`&.${textAreaClasses.textAreaDisabled}`]: {
                    color: tk(token.disabled.color),
                    borderColor: tk(token.disabled.borderColor),
                    backgroundColor: tk(token.disabled.backgroundColor),
                },
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                resize: resizable === true ? 'both' : resizable || 'none',
            },
            [`&.${textAreaClasses.allowClear}`]: {
                [`.${textAreaClasses.textArea}`]: {
                    paddingInlineEnd: tk(token.TextArea.allowClear.TextArea.paddingInlineEnd),
                },
            },
            [`.${textAreaClasses.affix}`]: {
                display: 'inline-flex',
                padding: 0,
                border: tk(token.affix.border),
                borderColor: tk(token.affix.borderColor),
                borderRadius: tk(token.TextArea.borderRadius[size]),
                boxSizing: 'border-box',
                alignItems: 'center',
                width: '100%',
                position: 'relative',
                [`&.${textAreaClasses.affixFocused}`]: {
                    outline: tk(token.Input.focus.outline),
                    outlineOffset: tk(token.Input.focus.outlineOffset),
                },
                [`&.${textAreaClasses.affixDisabled}`]: {
                    color: tk(token.disabled.color),
                    borderColor: tk(token.disabled.borderColor),
                    backgroundColor: tk(token.disabled.backgroundColor),
                },
                [`.${textAreaClasses.textArea}`]: {
                    border: 'none',
                    outline: 'none',
                },
                [`.${textAreaClasses.suffix}`]: {
                    fontSize: tk(token.fontSize.medium),
                    '.zmicon': {
                        display: 'inline-flex',
                        alignItems: 'center',
                    },
                },
                [`.${textAreaClasses.clearIcon}`]: {
                    cursor: 'pointer',
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    svg: {
                        width: 14,
                        height: 14,
                        viewBox: '0 0 14 14',
                    },
                    path: {
                        fill: tk(token.clearIcon.fill),
                    },
                    [`&.${textAreaClasses.clearIconHidden}`]: {
                        display: 'none',
                    },
                },
                [`.${textAreaClasses.showCount}`]: {
                    position: 'absolute',
                    [`&.${textAreaClasses.showCountPosition}-top-start`]: {
                        left: 0,
                        top: -22,
                    },
                    [`&.${textAreaClasses.showCountPosition}-top-end`]: {
                        right: 0,
                        top: -22,
                    },
                    [`&.${textAreaClasses.showCountPosition}-bottom-start`]: {
                        left: 0,
                        bottom: -22,
                    },
                    [`&.${textAreaClasses.showCountPosition}-bottom-end`]: {
                        right: 0,
                        bottom: -22,
                    },
                    color: tk(token.showCount.color),
                    lineHeight: tk(token.showCount.lineHeight),
                    fontFamily: tk(token.fontFamily),
                    fontSize: tk(token.showCount.fontSize),
                    fontWeight: tk(token.showCount.fontWeight),
                },
            },
            [`&.${textAreaClasses.rootReadOnly}`]: {
                [`.${textAreaClasses.affix}`]: {
                    color: tk(token.readOnly.color),
                    backgroundColor: tk(token.readOnly.backgroundColor),
                },
                [`.${textAreaClasses.textArea}:not(.${textAreaClasses.textAreaDisabled})`]: {
                    color: tk(token.readOnly.color),
                    backgroundColor: tk(token.readOnly.backgroundColor),
                },
                [`.${textAreaClasses.clearIcon}`]: {
                    display: 'none',
                },
            },
            [`&.${textAreaClasses.rootShowCount}`]: {
                [`&.${textAreaClasses.rootShowCountPosition}-bottom-start, &.${textAreaClasses.rootShowCountPosition}-bottom-end`]: {
                    paddingBottom: 20,
                },
                [`&.${textAreaClasses.rootShowCountPosition}-top-start, &.${textAreaClasses.rootShowCountPosition}-top-end`]: {
                    paddingTop: 20,
                },
            },
            ...statusStyle,
        },
    };
    return style;
}
